import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import './Home.css';

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatDuration(startIso) {
  if (!startIso) return '0h 0m';
  const diff = Math.floor((Date.now() - new Date(startIso)) / 1000);
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  return `${h}h ${m}m`;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
}

function getFirstName(fullName) {
  if (!fullName) return '';
  return fullName.trim().split(' ')[0];
}

function getInitials(fullName) {
  if (!fullName) return '?';
  return fullName.trim().split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

function calculateStreak(records) {
  if (!records || records.length === 0) return 0;
  const sorted = [...records]
    .filter(r => r.clock_in)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  let streak = 0;
  for (const r of sorted) {
    if (r.late_minutes === 0) streak++;
    else break;
  }
  return streak;
}

function calculatePerformance(records) {
  if (!records || records.length === 0) return 0;
  // Exclude holidays and approved leaves from performance calculation
  const workingDays  = records.filter(r => r.status !== 'public_holiday' && r.status !== 'approved_leave');
  const totalDays    = workingDays.length;
  const presentDays  = workingDays.filter(r => r.clock_in).length;
  const lateDays     = workingDays.filter(r => r.late_minutes > 0).length;
  const absentDays   = workingDays.filter(r => !r.clock_in).length;
  const attendanceScore  = totalDays > 0 ? (presentDays / totalDays) * 50 : 0;
  const punctualityScore = presentDays > 0 ? ((presentDays - lateDays) / presentDays) * 35 : 0;
  const absenceBonus     = absentDays === 0 ? 15 : Math.max(0, 15 - absentDays * 3);
  return Math.min(100, Math.round(attendanceScore + punctualityScore + absenceBonus));
}

function getPerformanceLabel(score) {
  if (score >= 90) return { label: 'Excellent', color: '#22c55e', emoji: '⭐' };
  if (score >= 75) return { label: 'Good',      color: '#3b82f6', emoji: '👍' };
  if (score >= 60) return { label: 'Average',   color: '#f59e0b', emoji: '📈' };
  return              { label: 'Needs Work',  color: '#ef4444', emoji: '⚠️' };
}

// ── Get display info for a record based on status ──
function getRecordDisplay(r) {
  const status = r.status;
  if (status === 'public_holiday') {
    return { iconCls: 'holiday', icon: '🎉', badgeCls: 'bholiday', badgeLbl: 'Holiday' };
  }
  if (status === 'approved_leave' || status === 'casual_auto') {
    return { iconCls: 'leave', icon: '📋', badgeCls: 'bleave', badgeLbl: 'On Leave' };
  }
  const hasIn  = !!r.clock_in;
  const isLate = hasIn && r.late_minutes > 0;
  if (!hasIn) return { iconCls: 'absent',  icon: '✗', badgeCls: 'babsent',  badgeLbl: 'Absent' };
  if (isLate) return { iconCls: 'late',    icon: '⏱', badgeCls: 'blate',    badgeLbl: 'Late' };
  return             { iconCls: 'present', icon: '✓', badgeCls: 'bpresent', badgeLbl: 'Present' };
}

function AnimNum({ value, duration = 600 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = parseInt(value) || 0;
    if (end === 0) { setDisplay(0); return; }
    const step = Math.ceil(end / (duration / 16));
    const timer = setInterval(() => {
      start = Math.min(start + step, end);
      setDisplay(start);
      if (start >= end) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{display}</span>;
}

function AttendanceRing({ rate, present, lates, absent }) {
  const r     = 38;
  const circ  = 2 * Math.PI * r;
  const gap   = 2;
  const total = present + lates + absent;
  const pArc  = total > 0 ? Math.max(0, (present / total) * circ - gap) : 0;
  const lArc  = total > 0 ? Math.max(0, (lates   / total) * circ - gap) : 0;
  const aArc  = total > 0 ? Math.max(0, (absent  / total) * circ - gap) : 0;
  const pOff  = circ;
  const lOff  = circ - (total > 0 ? (present / total) * circ : 0);
  const aOff  = lOff - (total > 0 ? (lates   / total) * circ : 0);
  return (
    <div className="home-ring-box">
      <svg width="96" height="96" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#f1f5f9" strokeWidth="9" />
        {present > 0 && <circle cx="50" cy="50" r={r} fill="none" stroke="#22c55e" strokeWidth="9" strokeDasharray={`${pArc} ${circ}`} strokeDashoffset={pOff} transform="rotate(-90 50 50)" />}
        {lates   > 0 && <circle cx="50" cy="50" r={r} fill="none" stroke="#f59e0b" strokeWidth="9" strokeDasharray={`${lArc} ${circ}`} strokeDashoffset={lOff} transform="rotate(-90 50 50)" />}
        {absent  > 0 && <circle cx="50" cy="50" r={r} fill="none" stroke="#ef4444" strokeWidth="9" strokeDasharray={`${aArc} ${circ}`} strokeDashoffset={aOff} transform="rotate(-90 50 50)" />}
        <text x="50" y="45" textAnchor="middle" fontSize="16" fontWeight="800" fill="#0f172a">{rate}%</text>
        <text x="50" y="57" textAnchor="middle" fontSize="8" fill="#94a3b8">RATE</text>
      </svg>
      <div className="home-ring-legend">
        {present > 0 && <span className="rleg rleg-green">●{present}P</span>}
        {lates   > 0 && <span className="rleg rleg-amber">●{lates}L</span>}
        {absent  > 0 && <span className="rleg rleg-red">●{absent}A</span>}
      </div>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`home-toast home-toast-${type}`}>
      <span className="home-toast-icon">{type === 'success' ? '✓' : type === 'error' ? '✗' : '⚠'}</span>
      <span>{message}</span>
    </div>
  );
}

export default function Home({ employee }) {
  const [clockedIn, setClockedIn]       = useState(false);
  const [currentTime, setCurrentTime]   = useState('');
  const [attendance, setAttendance]     = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [todayRecord, setTodayRecord]   = useState(null);
  const [loading, setLoading]           = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [duration, setDuration]         = useState('');
  const [toast, setToast]               = useState(null);
  const [shiftEnd, setShiftEnd]         = useState(null);
  const [clockOutWarning, setClockOutWarning] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [currentAnnIdx, setCurrentAnnIdx] = useState(0);
  const [warnings, setWarnings]         = useState([]);
  const [currentWarnIdx, setCurrentWarnIdx] = useState(0);
  const [warnLang, setWarnLang]         = useState('en'); // 'en' | 'ur'
  const [showStillWorking, setShowStillWorking] = useState(false);
  const [confirmingWork, setConfirmingWork] = useState(false);
  const durationRef                     = useRef(null);

  const now   = new Date();
  const month = now.getMonth() + 1;
  const year  = now.getFullYear();

  const getPKTDate = () => {
    const n = new Date();
    const utc = n.getTime() + n.getTimezoneOffset() * 60000;
    const pkt = new Date(utc + 5 * 3600000);
    return pkt.toISOString().split('T')[0];
  };
  const today = getPKTDate();

  useEffect(() => {
    const tick = () => setCurrentTime(new Date().toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true }));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    clearInterval(durationRef.current);
    if (todayRecord?.clock_in && !todayRecord?.clock_out) {
      setDuration(formatDuration(todayRecord.clock_in));
      durationRef.current = setInterval(() => setDuration(formatDuration(todayRecord.clock_in)), 1000);
    }
    return () => clearInterval(durationRef.current);
  }, [todayRecord]);

  useEffect(() => {
    if (!shiftEnd || !todayRecord?.clock_in || todayRecord?.clock_out) {
      setClockOutWarning(null);
      return;
    }
    const checkWarning = () => {
      const now = new Date();
      const [endH, endM] = shiftEnd.split(':').map(Number);
      const shiftEndMins = endH * 60 + endM;
      const currentMins  = now.getHours() * 60 + now.getMinutes();
      const minsLeft     = shiftEndMins - currentMins;
      if      (minsLeft <= 0)  setClockOutWarning(5);
      else if (minsLeft <= 5)  setClockOutWarning(4);
      else if (minsLeft <= 15) setClockOutWarning(3);
      else if (minsLeft <= 30) setClockOutWarning(2);
      else if (minsLeft <= 60) setClockOutWarning(1);
      else                     setClockOutWarning(null);
    };
    checkWarning();
    const t = setInterval(checkWarning, 60000);
    return () => clearInterval(t);
  }, [shiftEnd, todayRecord]);

  // ── "Still Working?" prompt — asks once past shift end + 30min grace, ──
  // instead of silently letting auto clock-out cut them off with no say.
  useEffect(() => {
    if (!shiftEnd || !todayRecord?.clock_in || todayRecord?.clock_out) {
      setShowStillWorking(false);
      return;
    }
    const checkStillWorking = () => {
      const nowT = new Date();
      const [endH, endM] = shiftEnd.split(':').map(Number);
      const shiftEndMins = endH * 60 + endM;
      const currentMins  = nowT.getHours() * 60 + nowT.getMinutes();
      const minsPast     = currentMins - shiftEndMins;

      // Same 30-minute threshold as the backend auto clock-out grace period
      if (minsPast < 30) { setShowStillWorking(false); return; }

      const todayKey    = getPKTDate();
      const dismissedKey = `still_working_dismissed_${todayKey}`;
      const alreadyHandled = sessionStorage.getItem(dismissedKey);
      setShowStillWorking(!alreadyHandled);
    };
    checkStillWorking();
    const t = setInterval(checkStillWorking, 60000);
    return () => clearInterval(t);
  }, [shiftEnd, todayRecord]);

  const handleStillWorking = async (isWorking) => {
    const todayKey = getPKTDate();
    sessionStorage.setItem(`still_working_dismissed_${todayKey}`, '1');
    setShowStillWorking(false);

    if (!isWorking) return; // they'll clock out themselves, or auto clock-out handles it

    setConfirmingWork(true);
    try {
      await fetch('https://fotileams.com/api/attendance/confirm-working', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      showToast('Got it — you have 2 more hours before we check again', 'success');
    } catch {
      // Non-critical — if this fails, normal 30-min grace auto clock-out still applies as a safety net
    } finally {
      setConfirmingWork(false);
    }
  };

  useEffect(() => { loadAll(); fetchAnnouncements(); fetchWarnings(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const res  = await fetch('https://fotileams.com/api/announcements/', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      const all  = data.announcements || [];
      if (all.length > 0) {
        const dismissed = JSON.parse(sessionStorage.getItem('dismissed_announcements') || '[]');
        const unseen    = all.filter(a => !dismissed.includes(a.id));
        if (unseen.length > 0) {
          setAnnouncements(unseen);
          setCurrentAnnIdx(0);
          setShowAnnouncement(true);
        }
      }
    } catch {}
  };

  const dismissAnnouncement = () => {
    const dismissed = JSON.parse(sessionStorage.getItem('dismissed_announcements') || '[]');
    dismissed.push(announcements[currentAnnIdx]?.id);
    sessionStorage.setItem('dismissed_announcements', JSON.stringify(dismissed));
    if (currentAnnIdx < announcements.length - 1) {
      setCurrentAnnIdx(i => i + 1);
    } else {
      setShowAnnouncement(false);
    }
  };

  // ── Employee-specific warnings (GPS spoofing / policy warnings from HR) ──
  const fetchWarnings = async () => {
    try {
      const res  = await fetch('https://fotileams.com/api/warnings/me', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      const all  = data.warnings || [];
      if (all.length > 0) {
        setWarnings(all);
        setCurrentWarnIdx(0);
      }
    } catch {}
  };

  const acknowledgeWarning = async () => {
    const current = warnings[currentWarnIdx];
    if (!current) return;
    try {
      await fetch(`https://fotileams.com/api/warnings/${current.id}/acknowledge`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
    } catch {}
    if (currentWarnIdx < warnings.length - 1) {
      setCurrentWarnIdx(i => i + 1);
    } else {
      setWarnings([]);
    }
  };

  const loadAll = async (background = false) => {
    try {
      if (!background) setLoading(true);
      const [attData, lvData, profileData] = await Promise.all([
        api.getMyAttendance(month, year),
        api.getLeaveBalance(),
        api.getMyProfile(),
      ]);
      if (profileData?.end_time) {
        setShiftEnd(profileData.end_time.substring(0, 5));
      }
      setAttendance(attData);
      setLeaveBalance(lvData.balance || null);

      const currentToday = getPKTDate();
      const todayRec = attData.records?.find(r => {
        if (!r.date && !r.date_str) return false;
        const d = r.date_str || r.date;
        return d.substring(0, 10) === currentToday;
      });

      if (todayRec) {
        setTodayRecord(todayRec);
        setClockedIn(!!(todayRec.clock_in && !todayRec.clock_out));
      } else if (!background) {
        setTodayRecord(null);
        setClockedIn(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (!background) setLoading(false);
    }
  };

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });

  const getFreshLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('no-gps')); return; }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true, timeout: 15000, maximumAge: 0,
    });
  });

  const handleClock = async () => {
    if (clockLoading) return;
    if (clockOutLocked) {
      showToast(`You can clock out in ${minutesUntilUnlock} more minute(s)`, 'warning');
      return;
    }
    setClockLoading(true);
    try {
      if (clockedIn) {
        const res = await api.clockOut();
        if (res.error) { showToast(res.error, 'error'); return; }
        showToast(`Clocked out · ${res.total_hours} total`, 'success');
        setTodayRecord(prev => prev ? { ...prev, clock_out: new Date().toISOString(), total_minutes: res.total_minutes || 0 } : prev);
        setClockedIn(false);
        loadAll();
      } else {
        let pos;
        try { pos = await getFreshLocation(); }
        catch (e) {
          if (e.code === 1) showToast('Location permission denied — enable GPS', 'error');
          else if (e.code === 3) showToast('GPS timed out — try near a window', 'error');
          else showToast('Could not get location — try again', 'error');
          return;
        }
        const res = await api.clockIn(pos.coords.latitude, pos.coords.longitude, null, null);
        if (res.error) {
          if (res.error.includes('Already clocked in')) {
            setClockedIn(true);
            showToast('You are already clocked in today', 'warning');
            loadAll(true);
          } else {
            showToast(res.reasons?.[0] || res.error, 'error');
          }
          return;
        }
        const lateMsg = res.late_minutes > 0 ? ` · ${res.late_minutes}m late` : ' · On time ✓';
        showToast(`Clocked in at ${formatTime(new Date())}${lateMsg}`, res.late_minutes > 0 ? 'warning' : 'success');
        setTodayRecord({
          clock_in: new Date().toISOString(),
          clock_out: null,
          late_minutes: res.late_minutes || 0,
          status: res.status || 'present',
          date: today,
        });
        setClockedIn(true);
        loadAll(true);
      }
    } catch { showToast('Something went wrong — try again', 'error'); }
    finally { setClockLoading(false); }
  };

  const records        = attendance?.records || [];
  const recent         = records.slice(0, 5);

  // ── Fix: exclude holidays and approved leaves from absent/present counts ──
  const workingRecords = records.filter(r => r.status !== 'public_holiday' && r.status !== 'approved_leave' && r.status !== 'casual_auto');
  const presentDays    = workingRecords.filter(r => !!r.clock_in && r.late_minutes === 0).length;
  const lateDays       = workingRecords.filter(r => !!r.clock_in && r.late_minutes > 0).length;
  const absentDays     = workingRecords.filter(r => !r.clock_in).length;
  const totalTracked   = presentDays + lateDays + absentDays;
  const attendanceRate = totalTracked > 0 ? Math.round(((presentDays + lateDays) / totalTracked) * 100) : 0;

  const streak         = calculateStreak(records);
  const perfScore      = calculatePerformance(records);
  const perfLabel      = getPerformanceLabel(perfScore);
  const isLateToday    = todayRecord?.late_minutes > 0;

  // ── Clock-out lock — prevents accidental double-tap clock-outs ──────────
  // Re-computed fresh on every render; the existing duration ticker (every
  // second while clocked in) naturally keeps this live without extra timers.
  const MIN_MINUTES_BEFORE_CLOCKOUT = 60;
  const minutesSinceClockIn = todayRecord?.clock_in
    ? Math.floor((Date.now() - new Date(todayRecord.clock_in).getTime()) / 60000)
    : 0;
  const clockOutLocked   = clockedIn && minutesSinceClockIn < MIN_MINUTES_BEFORE_CLOCKOUT;
  const minutesUntilUnlock = clockOutLocked ? MIN_MINUTES_BEFORE_CLOCKOUT - minutesSinceClockIn : 0;

  const leaves = leaveBalance ? [
    { type: 'Casual', remaining: leaveBalance.casual?.remaining ?? 3,  total: leaveBalance.casual?.total ?? 3,  color: '#ef4444' },
    { type: 'Sick',   remaining: leaveBalance.sick?.remaining   ?? 5,  total: leaveBalance.sick?.total   ?? 5,  color: '#22c55e' },
    { type: 'Annual', remaining: leaveBalance.annual?.remaining ?? 12, total: leaveBalance.annual?.total ?? 12, color: '#3b82f6' },
  ] : [];

  return (
    <div className="home-root">

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Employee-specific warnings take priority over general announcements */}
      {warnings.length > 0 && warnings[currentWarnIdx] && (() => {
        const w = warnings[currentWarnIdx];
        return (
          <div className="ann-popup-overlay">
            <div className="warn-popup" style={{ borderTop: '4px solid #ef4444' }}>
              <div className="ann-popup-icon">⚠️</div>
              <div className="ann-popup-title" style={{ color: '#b91c1c' }}>Warning · انتباہ</div>

              <div className="warn-lang-toggle">
                <button
                  className={`warn-lang-btn ${warnLang === 'en' ? 'warn-lang-active' : ''}`}
                  onClick={() => setWarnLang('en')}
                >English</button>
                <button
                  className={`warn-lang-btn ${warnLang === 'ur' ? 'warn-lang-active' : ''}`}
                  onClick={() => setWarnLang('ur')}
                >اردو</button>
              </div>

              <div
                className={`ann-popup-message ${warnLang === 'ur' ? 'warn-msg-urdu' : ''}`}
                dir={warnLang === 'ur' ? 'rtl' : 'ltr'}
              >
                {warnLang === 'ur' ? w.message_ur : w.message_en}
              </div>

              {warnings.length > 1 && (
                <div className="ann-popup-count">{currentWarnIdx + 1} of {warnings.length}</div>
              )}
              <button className="ann-popup-btn" style={{ background: '#ef4444' }} onClick={acknowledgeWarning}>
                {currentWarnIdx < warnings.length - 1 ? 'Next →' : 'I Understand · سمجھ گیا'}
              </button>
            </div>
          </div>
        );
      })()}

      {warnings.length === 0 && showAnnouncement && announcements[currentAnnIdx] && (() => {
        const ann = announcements[currentAnnIdx];
        const colors = { urgent: '#ef4444', holiday: '#f59e0b', event: '#8b5cf6', info: '#3b82f6' };
        const icons  = { urgent: '🚨', holiday: '🎉', event: '🎊', info: '📢' };
        const color  = colors[ann.type] || colors.info;
        return (
          <div className="ann-popup-overlay">
            <div className="ann-popup" style={{ borderTop: `4px solid ${color}` }}>
              <div className="ann-popup-icon">{icons[ann.type] || '📢'}</div>
              <div className="ann-popup-title">{ann.title}</div>
              <div className="ann-popup-message">{ann.message}</div>
              {announcements.length > 1 && (
                <div className="ann-popup-count">{currentAnnIdx + 1} of {announcements.length}</div>
              )}
              <button className="ann-popup-btn" style={{ background: color }} onClick={dismissAnnouncement}>
                {currentAnnIdx < announcements.length - 1 ? 'Next →' : 'Got it ✓'}
              </button>
            </div>
          </div>
        );
      })()}

      {warnings.length === 0 && !showAnnouncement && showStillWorking && (
        <div className="ann-popup-overlay">
          <div className="still-working-popup" style={{ borderTop: '4px solid #3b82f6' }}>
            <div className="ann-popup-icon">🕐</div>
            <div className="ann-popup-title">Still Working?</div>
            <div className="ann-popup-message">
              Your shift ended at {shiftEnd ? formatTime(new Date(`2000-01-01T${shiftEnd}:00`)) : 'the scheduled time'} and you're still clocked in.
              Are you still working right now?
            </div>
            <div className="still-working-btns">
              <button className="sw-btn sw-btn-no" onClick={() => handleStillWorking(false)}>
                No, I forgot to clock out
              </button>
              <button className="sw-btn sw-btn-yes" onClick={() => handleStillWorking(true)} disabled={confirmingWork}>
                {confirmingWork ? 'Confirming…' : "Yes, I'm still working"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="home-hero">
        <div className="home-greeting-row">
          <div className="home-avatar">{getInitials(employee?.full_name)}</div>
          <div className="home-greeting-text">
            <span className="home-greeting">{getGreeting()},</span>
            <span className="home-name">{getFirstName(employee?.full_name)} 👋</span>
          </div>
          {streak > 0 && (
            <div className="home-streak-badge">🔥 {streak} day{streak > 1 ? 's' : ''}</div>
          )}
        </div>

        <div className="home-clock-wrap">
          <div className="home-clock-time">
            <span className="home-clock-hm">{currentTime?.replace(/\s?(am|pm)/i, '')}</span>
            <span className="home-clock-period">{currentTime?.match(/am|pm/i)?.[0]?.toUpperCase() || ''}</span>
          </div>
          <div className="home-clock-date">
            {now.toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>

        <div className="home-status-row">
          {clockedIn ? (
            <div className="home-status-chip home-chip-in">
              <span className="home-status-pulse" />
              In since {formatTime(todayRecord?.clock_in)} · {duration}
            </div>
          ) : todayRecord?.clock_out ? (
            <div className="home-status-chip home-chip-done">
              ✓ Completed · {Math.floor((todayRecord.total_minutes||0)/60)}h {(todayRecord.total_minutes||0)%60}m
            </div>
          ) : (
            <div className="home-status-chip home-chip-out">○ Not clocked in yet</div>
          )}
          {isLateToday && (
            <div className="home-late-chip">⏱ {todayRecord.late_minutes}m late</div>
          )}
        </div>

        {!todayRecord?.clock_out && (
          <button
            className={`home-clock-btn ${clockedIn ? 'home-btn-out' : 'home-btn-in'} ${clockLoading ? 'home-btn-loading' : ''} ${clockOutLocked ? 'home-btn-locked' : ''}`}
            onClick={handleClock} disabled={clockLoading || clockOutLocked}
          >
            {clockLoading ? 'Please wait…'
              : clockOutLocked ? `🔒 Clock Out available in ${minutesUntilUnlock}m`
              : clockedIn ? 'Clock Out' : 'Clock In'}
          </button>
        )}

        {todayRecord?.clock_out && (
          <div className="home-done-msg">✓ You've completed your shift today</div>
        )}

        {clockOutWarning && !todayRecord?.clock_out && (
          <div className={`home-clockout-warning home-cow-${clockOutWarning}`}>
            <span className="home-cow-icon">
              {clockOutWarning === 5 ? '🚨' : clockOutWarning === 4 ? '🔴' : '⚠️'}
            </span>
            <div className="home-cow-text">
              <div className="home-cow-title">
                {clockOutWarning === 5 ? 'SHIFT ENDED — Clock out immediately!' :
                 clockOutWarning === 4 ? '5 minutes left — Clock out soon!' :
                 clockOutWarning === 3 ? "15 minutes left — Don't forget to clock out" :
                 clockOutWarning === 2 ? '30 minutes left — Reminder to clock out' :
                                         '1 hour left — Your shift ends soon'}
              </div>
              <div className="home-cow-sub">
                Warning {clockOutWarning}/5 — Failure to clock out may result in auto clock-out
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="home-content">

        {isLateToday && (
          <div className="home-notice">
            <span>⏱</span>
            <div>
              <div className="home-notice-title">Late arrival recorded</div>
              <div className="home-notice-sub">
                {todayRecord.late_minutes} minutes late today.
                {todayRecord.late_minutes >= 180 ? ' Full day deduction may apply.' :
                 todayRecord.late_minutes >= 60  ? ' Half day deduction risk.' : ''}
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <div className="home-perf-row">
            <div className="home-perf-card">
              <div className="home-perf-top">
                <span className="home-perf-emoji">{perfLabel.emoji}</span>
                <span className="home-perf-label" style={{ color: perfLabel.color }}>{perfLabel.label}</span>
              </div>
              <div className="home-perf-score" style={{ color: perfLabel.color }}>
                <AnimNum value={perfScore} />
                <span className="home-perf-unit">/100</span>
              </div>
              <div className="home-perf-sub">Performance Score</div>
              <div className="home-perf-bar">
                <div className="home-perf-fill" style={{ width: `${perfScore}%`, background: perfLabel.color }} />
              </div>
            </div>
            <div className="home-streak-card">
              <div className="home-streak-fire">{streak > 0 ? '🔥' : '❄️'}</div>
              <div className="home-streak-num"><AnimNum value={streak} /></div>
              <div className="home-streak-lbl">Day{streak !== 1 ? 's' : ''} On Time</div>
              <div className="home-streak-sub">
                {streak === 0 ? 'Start your streak!' :
                 streak < 5  ? 'Keep it up!' :
                 streak < 10 ? 'Great streak! 💪' : 'Unstoppable! 🏆'}
              </div>
            </div>
          </div>
        )}

        <div className="home-section-label">{now.toLocaleString('default', { month: 'long' })} {year}</div>

        {loading ? (
          <div className="home-loading">Loading your data…</div>
        ) : (
          <>
            <div className="home-stats-row">
              <div className="home-stats-grid">
                <div className="home-stat home-stat-green">
                  <span className="home-stat-num"><AnimNum value={presentDays} /></span>
                  <span className="home-stat-lbl">Present</span>
                </div>
                <div className="home-stat home-stat-red">
                  <span className="home-stat-num"><AnimNum value={absentDays} /></span>
                  <span className="home-stat-lbl">Absent</span>
                </div>
                <div className="home-stat home-stat-amber">
                  <span className="home-stat-num"><AnimNum value={lateDays} /></span>
                  <span className="home-stat-lbl">Lates</span>
                </div>
              </div>
              <div className="home-ring-wrap">
                <AttendanceRing rate={attendanceRate} present={presentDays} lates={lateDays} absent={absentDays} />
              </div>
            </div>

            {leaves.length > 0 && (
              <>
                <div className="home-section-label">Leave Balance</div>
                <div className="home-card">
                  {leaves.map((l, i) => (
                    <div key={i} className="home-leave-item">
                      <div className="home-leave-top">
                        <span className="home-leave-type">{l.type} Leave</span>
                        <span className="home-leave-count" style={{ color: l.color }}>{l.remaining} / {l.total} days</span>
                      </div>
                      <div className="home-leave-bar">
                        <div className="home-leave-fill" style={{ width: `${Math.max(0, (l.remaining / l.total) * 100)}%`, background: l.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="home-section-label">Recent Activity</div>
            <div className="home-card">
              {recent.length === 0 ? (
                <div className="home-empty">No records this month</div>
              ) : recent.map((r, i) => {
                const { iconCls, icon, badgeCls, badgeLbl } = getRecordDisplay(r);
                const ongoing = r.clock_in && !r.clock_out;
                const isLate  = r.clock_in && r.late_minutes > 0;
                return (
                  <div key={i} className="home-act-item">
                    <div className={`home-act-icon ${iconCls}`}>{icon}</div>
                    <div className="home-act-info">
                      <div className="home-act-date">
                        {new Date(r.date).toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric', month: 'short' })}
                        {isLate && <span className="home-act-late">+{r.late_minutes}m</span>}
                      </div>
                      <div className="home-act-times">
                        {r.clock_in ? `${formatTime(r.clock_in)} → ${ongoing ? 'ongoing' : formatTime(r.clock_out)}` : '—'}
                      </div>
                    </div>
                    <div className="home-act-right">
                      <div className="home-act-hours">
                        {ongoing ? duration : r.total_minutes ? `${Math.floor(r.total_minutes/60)}h ${r.total_minutes%60}m` : '—'}
                      </div>
                      <span className={`badge ${badgeCls}`}>{badgeLbl}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}